import { useIsScribeBlockEditorCodeblockEnabled } from '@/app-shell/flags/session-flags';
import { useIsSelectionAskHeidiEnabled } from '@/app-shell/flags/shell-flags';
import { EditorAnalyticsProvider } from '@/components/tiptap/shared/editor-analytics-provider';
import { getCompactMenuSuggestionCount } from '@/components/tiptap/shared/selection-ai-actions.utils';
import { TextMenuWrapper } from '@/components/tiptap/shared/text-menu-base';
import {
  BoldButton,
  CodeBlockButton,
  ConvertToSectionButton,
  CopyButton,
  FindReplaceButton,
  ItalicButton,
  SnippetButton,
  UnderlineButton,
} from '@/components/tiptap/shared/text-menu-buttons';
import { useSelectionAiMenuShownTracking } from '@/components/tiptap/shared/use-selection-ai-menu-shown-tracking';
import { useSelectionCapture } from '@/components/tiptap/shared/use-selection-capture';
import { isSelectionAiEnabledForEditor } from '@/components/tiptap/vMarkdown/editor-type';
import { useEmphasisDisabledTooltip } from '@/features/copySettings';
import { type EditorType } from '@heidi/editor/core';
import { type Editor } from '@tiptap/react';
import { Fragment } from 'react';
import { useTextMenuCommands } from './hooks/useTextmenuCommands';
import { useTextMenuStates } from './hooks/useTextmenuStates';
import { SelectionAskHeidiToolbar } from './selection-ask-heidi-toolbar';

export const TextMenu = ({
  editor,
  editorType,
  emphasisDisabled = false,
  enableFindReplace,
  enableSnippet,
  enableConvertToSection,
  selector,
}: {
  editor: Editor;
  editorType?: EditorType;
  /** Explicit NoteTab scope; EditorType.NOTE is also used by Haiku. */
  emphasisDisabled?: boolean;
  enableFindReplace: boolean;
  enableSnippet: boolean;
  enableConvertToSection: boolean;
  selector: string;
}) => {
  const {
    onBold,
    onItalic,
    onCopy,
    onFindReplace,
    onCreateSnippet,
    onUnderline,
    onCodeBlock,
    onConvertToSection,
  } = useTextMenuCommands(editor, editorType);
  const {
    isHeading,
    isBold,
    isItalic,
    isUnderline,
    isCodeBlock,
    canConvertToSection,
    shouldShow,
  } = useTextMenuStates({
    editor,
    selector,
  });
  const codeBlockEnabled = useIsScribeBlockEditorCodeblockEnabled();
  const isSelectionAskHeidiEnabled = useIsSelectionAskHeidiEnabled();
  const showSelectionAi = isSelectionAiEnabledForEditor(
    isSelectionAskHeidiEnabled,
    editorType
  );
  const { trackMenuShown, trackMenuHidden } = useSelectionAiMenuShownTracking({
    editor,
    suggestionCount: getCompactMenuSuggestionCount(),
  });
  useSelectionCapture(editor, showSelectionAi);
  // Supplied by the host because EditorType.NOTE is broader than NoteTab.
  const emphasisDisabledTooltip = useEmphasisDisabledTooltip();

  const selectionEditorActions = {
    enableFindReplace,
    enableSnippet,
    enableConvertToSection: enableConvertToSection && canConvertToSection,
    onFindReplace,
    onCreateSnippet,
    onConvertToSection,
  };

  if (showSelectionAi) {
    return (
      <EditorAnalyticsProvider editorType="block_markdown">
        <SelectionAskHeidiToolbar
          editor={editor}
          shouldShow={shouldShow}
          onShow={trackMenuShown}
          onHide={trackMenuHidden}
          isHeading={isHeading}
          isBold={isBold}
          isItalic={isItalic}
          isUnderline={isUnderline}
          emphasisDisabled={emphasisDisabled}
          emphasisDisabledTooltip={emphasisDisabledTooltip}
          onBold={onBold}
          onItalic={onItalic}
          onUnderline={onUnderline}
          onCopy={onCopy}
          editorActions={selectionEditorActions}
        />
      </EditorAnalyticsProvider>
    );
  }

  return (
    <EditorAnalyticsProvider editorType="block_markdown">
      <TextMenuWrapper editor={editor} shouldShow={shouldShow}>
        <Fragment>
          <BoldButton
            onClick={onBold}
            active={isBold}
            disabled={emphasisDisabled}
            disabledTooltip={emphasisDisabledTooltip}
          />
          <ItalicButton
            onClick={onItalic}
            active={isItalic}
            disabled={emphasisDisabled}
            disabledTooltip={emphasisDisabledTooltip}
          />
          <UnderlineButton
            onClick={onUnderline}
            active={isUnderline}
            disabled={emphasisDisabled}
            disabledTooltip={emphasisDisabledTooltip}
          />
          {codeBlockEnabled && (
            <CodeBlockButton onClick={onCodeBlock} active={isCodeBlock} />
          )}
          {enableConvertToSection && canConvertToSection && (
            <ConvertToSectionButton onClick={onConvertToSection} />
          )}
          <CopyButton onClick={onCopy} />
          {enableFindReplace && (
            <FindReplaceButton
              onClick={onFindReplace}
              editor={editor}
              data-testid="text-menu-find-replace"
            />
          )}
          {enableSnippet && <SnippetButton onClick={onCreateSnippet} editor={editor} />}
        </Fragment>
      </TextMenuWrapper>
    </EditorAnalyticsProvider>
  );
};
